import React, { useMemo, useState } from 'react';
import { Variable } from 'src/api/types';
import useVariables from 'src/hooks/useVariables';
import { VariablesWorkspaceModal } from 'src/components/modals/VariablesWorkspaceModal';
import { getVariableIcon } from 'src/components/FormulaInput/comps/variableIcon';
import { isWorkspaceOrGlobalVariable } from 'src/components/FormulaInput/shared/formula';
import { useRiskIntel } from './useRiskIntel';
import { PositionedVariablesPopover } from './comps/PositionedVariablesPopover';
import { IntelVariable } from './types';

/**
 * Shared "edit variables" popover wiring for the AI wizard's formula surfaces (Impact step, Review
 * step). Owns the anchored popover (PositionedVariablesPopover) + the "+ variable" modal, so each
 * step just calls `openVariables(anchorEl, variableId?)` on a pill click and renders `popoverNode`.
 */
export const useVariablesPopover = () => {
  const { session, pending, populateVariable, assessmentId, assessmentName } = useRiskIntel();
  const variables = session?.variables ?? [];
  const { variables: fullVariables, invalidate } = useVariables({ assessmentId: assessmentId ?? '', wvOverride: true });

  // Reuse the scenario-form's per-type icons: resolve each session variable to its full Variable in
  // the live workspace/assessment set and run the same getVariableIcon (workspace / assessment /
  // override / global). Falls back to a scope-synthesised variable if it isn't in the live set yet.
  const workspaceVariableNames = useMemo(
    () => new Set((fullVariables ?? []).filter(isWorkspaceOrGlobalVariable).map((v) => v.name)),
    [fullVariables],
  );
  const renderIcon = (iv: IntelVariable): React.ReactNode => {
    const full =
      (fullVariables ?? []).find((v) => v.id === iv.id) ??
      ({ id: iv.id, name: iv.name, workspace_variable: iv.scope === 'workspace', isGlobal: false } as Variable);
    return getVariableIcon(full, workspaceVariableNames);
  };

  const [popover, setPopover] = useState<{ anchorEl: HTMLElement; editVarId: number | null } | null>(null);
  const [modalScope, setModalScope] = useState<'workspace' | 'assessment' | null>(null);

  const openVariables = (anchorEl: HTMLElement, editVarId: number | null = null) =>
    setPopover({ anchorEl, editVarId });
  const closeVariables = () => setPopover(null);

  const popoverNode = (
    <>
      {popover && (
        <PositionedVariablesPopover
          anchorEl={popover.anchorEl}
          variables={variables}
          pending={pending}
          initialEditId={popover.editVarId}
          onPopulate={(id, value, currency) => populateVariable(id, value, currency)}
          onAddWorkspaceVariable={() => setModalScope('workspace')}
          onAddAssessmentVariable={() => setModalScope('assessment')}
          renderIcon={renderIcon}
          onClose={closeVariables}
        />
      )}
      {modalScope && (
        <VariablesWorkspaceModal
          onClose={() => {
            invalidate();
            setModalScope(null);
          }}
          scope={
            modalScope === 'workspace'
              ? { type: 'workspace' }
              : { type: 'assessment', id: assessmentId ?? '', name: assessmentName ?? '' }
          }
          initHideEmptyVariables={false}
        />
      )}
    </>
  );

  return { openVariables, closeVariables, isOpen: !!popover, popoverNode };
};
